import {
  GOODS_RECEIPT_STATUS_LABELS,
  GOODS_RECEIPT_STATUSES,
  type GoodsReceipt,
  goodsReceiptStatusSchema,
  RECEIPT_COST_STATUS_LABELS,
  RECEIPT_COST_STATUSES,
  receiptCostStatusSchema,
} from "@station/contracts";
import { Decimal } from "@station/core";
import {
  Alert,
  Button,
  Card,
  DataTable,
  type DataTableColumn,
  EmptyState,
  FormField,
  MoneyText,
  Select,
  SkeletonRows,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { PackageCheck, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { CounterpartyIdCombobox } from "../../../../features/counterparties/counterparty-combobox.js";
import {
  formatStockQuantity,
  moneyExport,
  quantityExport,
} from "../../../../features/market/market-values.js";
import {
  type GoodsReceiptFilters,
  goodsReceiptsQuery,
  useGoodsReceipts,
} from "../../../../features/market/purchasing-queries.js";
import { useMarketPermissions } from "../../../../features/market/queries.js";
import { ReceiptDialog } from "../../../../features/market/receipt-dialog.js";
import {
  GoodsReceiptStatusBadge,
  MarketPage,
  marketSearchShape,
  MarketStationField,
  NoStations,
  ReceiptCostBadge,
  useMarketStations,
} from "../../../../features/market/shared.js";
import { compactSearch } from "../../../../features/shifts/shift-values.js";
import { quickActionSearch, useOpenFromQuickAction } from "../../../../lib/hooks.js";
import { api } from "../../../../lib/api.js";
import { useCursorPages } from "../../../../lib/cursor-pages.js";
import { errorMessage } from "../../../../lib/errors.js";
import { exportRowsToExcel, fetchAllPages } from "../../../../lib/excel.js";
import { formatDate } from "../../../../lib/format.js";

const searchSchema = z.object({
  ...marketSearchShape,
  status: goodsReceiptStatusSchema.optional().catch(undefined),
  cost: receiptCostStatusSchema.optional().catch(undefined),
  supplier: z.string().uuid().optional().catch(undefined),
  ...quickActionSearch,
});

export const Route = createFileRoute("/_app/market/mal-kabul/")({
  validateSearch: (search) => searchSchema.parse(search),
  component: GoodsReceiptsRoute,
});

/**
 * Mal kabuller (Prompt 16 — web, aşama B; ADR 0016 §2). Siparişten ya da serbest açılır, tablet ekranında barkodla
 * okutulur; kaydedilince sağlam miktar GEÇİCİ maliyetle stoğa girer (fiş YOK — alış fişini fatura keser), faturayla
 * eşleşince maliyet KESİNLEŞİR. Okuma `market.read`, yazma `market.receipt.manage`.
 */
function GoodsReceiptsRoute(): ReactElement {
  const { t } = useTranslation();
  return (
    <MarketPage
      title={t("menu.marketGoodsReceipts")}
      description={t("market.receipts.description")}
      breadcrumb={[{ label: t("menu.marketGoodsReceipts") }]}
    >
      {({ companyId }) => <GoodsReceiptsBody companyId={companyId} />}
    </MarketPage>
  );
}

function GoodsReceiptsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const search = Route.useSearch();
  const permissions = useMarketPermissions();
  const station = useMarketStations(companyId);
  const [creating, setCreating] = useState(false);
  useOpenFromQuickAction(
    search.yeni === "1",
    () => setCreating(true),
    permissions.canManageReceipts && !station.isLoading && station.stations.length > 0,
  );

  const setSearch = (patch: Record<string, unknown>) =>
    void navigate({
      to: ".",
      search: ((previous: Record<string, unknown>) => compactSearch({ ...previous, ...patch })) as never,
      replace: true,
    });

  const filters: GoodsReceiptFilters = {
    stationId: station.stationId ?? undefined,
    supplierId: search.supplier,
    status: search.status,
    costStatus: search.cost,
  };
  const pages = useCursorPages(JSON.stringify(filters));
  const list = useGoodsReceipts(companyId, filters, pages.cursor);

  const columns = useMemo<DataTableColumn<GoodsReceipt>[]>(
    () => [
      {
        id: "receiptNo",
        header: t("market.receipts.fields.receiptNo"),
        hideable: false,
        exportValue: (row) => row.receiptNo,
        cell: (row) => (
          <Link
            to="/market/mal-kabul/$id"
            params={{ id: row.id }}
            className="grid gap-0.5 underline underline-offset-4"
          >
            <span className="font-mono whitespace-nowrap">{row.receiptNo}</span>
            <span className="text-xs text-muted-foreground">{formatDate(row.businessDate)}</span>
          </Link>
        ),
      },
      {
        id: "despatchNo",
        header: t("market.receipts.fields.despatchNo"),
        exportValue: (row) => row.despatchNo,
        cell: (row) => <span className="font-mono text-xs whitespace-nowrap">{row.despatchNo ?? "—"}</span>,
      },
      {
        id: "supplier",
        header: t("market.orders.fields.supplier"),
        exportValue: (row) => row.supplierName,
        // İstasyon tedarikçinin altında küçük yazılır: sütun sayısı 820 px'te de okunur kalsın.
        cell: (row) => (
          <span className="grid min-w-40 gap-0.5">
            <span>{row.supplierName}</span>
            <span className="text-xs text-muted-foreground">{row.stationName}</span>
          </span>
        ),
      },
      {
        id: "station",
        header: t("market.fields.station"),
        defaultVisible: false,
        exportValue: (row) => row.stationName,
        cell: (row) => row.stationName,
      },
      {
        id: "order",
        header: t("market.receipts.fields.order"),
        exportValue: (row) => row.purchaseOrderNo,
        cell: (row) =>
          row.purchaseOrderId === null ? (
            <span className="text-muted-foreground">{t("market.receipts.free")}</span>
          ) : (
            <Link
              to="/market/siparisler/$id"
              params={{ id: row.purchaseOrderId }}
              className="font-mono text-xs underline underline-offset-4"
            >
              {row.purchaseOrderNo}
            </Link>
          ),
      },
      {
        id: "status",
        header: t("common.status"),
        exportValue: (row) => GOODS_RECEIPT_STATUS_LABELS[row.status],
        cell: (row) => <GoodsReceiptStatusBadge status={row.status} />,
      },
      {
        id: "cost",
        header: t("market.receipts.fields.cost"),
        exportValue: (row) => RECEIPT_COST_STATUS_LABELS[row.costStatus],
        cell: (row) => (row.status === "POSTED" ? <ReceiptCostBadge status={row.costStatus} /> : "—"),
      },
      {
        id: "received",
        header: t("market.receipts.fields.received"),
        align: "right",
        exportValue: (row) => quantityExport(row.totals.receivedQuantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.totals.receivedQuantity)}</span>,
      },
      {
        id: "damaged",
        header: t("market.receipts.fields.damaged"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) => quantityExport(row.totals.damagedQuantity),
        cell: (row) => <span className="tabular">{formatStockQuantity(row.totals.damagedQuantity)}</span>,
      },
      {
        id: "shortOver",
        header: t("market.receipts.fields.shortOver"),
        align: "right",
        defaultVisible: false,
        exportValue: (row) =>
          `${quantityExport(row.totals.shortQuantity)} / ${quantityExport(row.totals.overQuantity)}`,
        cell: (row) =>
          Decimal.from(row.totals.shortQuantity).isZero() &&
          Decimal.from(row.totals.overQuantity).isZero() ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <span className="tabular whitespace-nowrap">
              −{formatStockQuantity(row.totals.shortQuantity)} / +
              {formatStockQuantity(row.totals.overQuantity)}
            </span>
          ),
      },
      {
        id: "netAmount",
        header: t("market.receipts.fields.netAmount"),
        align: "right",
        exportValue: (row) => moneyExport(row.netAmount),
        cell: (row) => <MoneyText value={row.netAmount} className="font-medium" />,
      },
      {
        id: "match",
        header: t("market.receipts.fields.match"),
        exportValue: (row) => (row.matchId ? t("common.yes") : t("common.no")),
        cell: (row) =>
          row.matchId === null ? (
            <span className="text-muted-foreground">—</span>
          ) : (
            <Link
              to="/market/eslestirme/$id"
              params={{ id: row.matchId }}
              className="text-sm underline underline-offset-4"
            >
              {t("market.receipts.openMatch")}
            </Link>
          ),
      },
    ],
    [t],
  );

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stations.length === 0) return <NoStations />;

  return (
    <>
      <Card className="flex flex-wrap items-end gap-3 p-4">
        <MarketStationField selection={station} />
        {permissions.canManageReceipts ? (
          <Button className="ms-auto" onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("market.receipts.new")}
          </Button>
        ) : null}
      </Card>

      <DataTable
        caption={t("market.receipts.caption")}
        columns={columns}
        rows={list.data?.items ?? []}
        rowKey={(row) => row.id}
        loading={list.isPending}
        error={list.error ? <Alert tone="danger">{errorMessage(list.error)}</Alert> : undefined}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<PackageCheck />}
            title={t("market.receipts.emptyTitle")}
            description={t("market.receipts.emptyDescription")}
          />
        }
        filters={
          <>
            <FormField label={t("common.status")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.status ?? ""}
                  onChange={(event) => setSearch({ status: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {GOODS_RECEIPT_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {GOODS_RECEIPT_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("market.receipts.fields.cost")} className="w-44">
              {(control) => (
                <Select
                  {...control}
                  value={search.cost ?? ""}
                  onChange={(event) => setSearch({ cost: event.target.value || undefined })}
                >
                  <option value="">{t("common.all")}</option>
                  {RECEIPT_COST_STATUSES.map((status) => (
                    <option key={status} value={status}>
                      {RECEIPT_COST_STATUS_LABELS[status]}
                    </option>
                  ))}
                </Select>
              )}
            </FormField>
            <FormField label={t("market.orders.fields.supplier")} className="w-72">
              {(control) => (
                <CounterpartyIdCombobox
                  {...control}
                  companyId={companyId}
                  counterpartyId={search.supplier ?? null}
                  placeholder={t("market.suggestions.allSuppliers")}
                  onChange={(supplierId) => setSearch({ supplier: supplierId ?? undefined })}
                />
              )}
            </FormField>
          </>
        }
        pagination={{
          hasPrevious: pages.hasPrevious,
          hasNext: list.data?.page.hasMore ?? false,
          onPrevious: pages.previous,
          onNext: () => pages.next(list.data?.page.nextCursor),
          pageLabel: t("common.pageNumber", { page: pages.pageNumber }),
        }}
        onExport={async (visible) => {
          const rows = await fetchAllPages<GoodsReceipt>(
            async (cursor) =>
              (
                await api.GET("/market/goods-receipts", {
                  params: { query: goodsReceiptsQuery(companyId, filters, cursor, 200) as never },
                })
              ).data!,
          );
          await exportRowsToExcel({
            fileName: "mal-kabuller",
            sheetName: t("menu.marketGoodsReceipts"),
            columns: visible,
            rows,
          });
        }}
      />

      {permissions.canManageReceipts ? (
        <ReceiptDialog
          open={creating}
          onOpenChange={setCreating}
          companyId={companyId}
          stations={station.stations}
          defaultStationId={station.stationId}
          onSaved={(saved) => void navigate({ to: "/market/mal-kabul/$id", params: { id: saved.id } })}
        />
      ) : null}
    </>
  );
}
